import React, { useState, useEffect } from "react";
import { Form, Button, Row, Col, Spinner, Alert } from "react-bootstrap";
import Select from "react-select";
import axios from "axios";
import paths from "routes/paths";
import { toast } from "react-toastify";
import ThemedSelect from "components/commonbreadcrumb/ThemedSelect";

const MobileNotification = () => {
  const [facilities, setFacilities] = useState({});
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reminderData, setReminderData] = useState(null);

  const defaultReminders = [
    { user: null, sendEmail: false, sendPush: false },
    { user: null, sendEmail: false, sendPush: false },
    { user: null, sendEmail: false, sendPush: false },
  ];

  const [reminderType, setReminderType] = useState("reminder_1");
  const [reminderMinutes, setReminderMinutes] = useState(15);
  const [reminders, setReminders] = useState(defaultReminders);
  const [allReminders, setAllReminders] = useState({
    reminder_1: defaultReminders,
    reminder_2: defaultReminders,
    reminder_3: defaultReminders,
  });


  useEffect(() => {
    const storedFacilities = JSON.parse(localStorage.getItem("facilitiesData"));
    if (storedFacilities) {
      setFacilities(storedFacilities);
    }
  }, []);

  const mapReminderUsers = (users = []) => {
    const mapped = defaultReminders.map(() => ({ user: null, sendEmail: false, sendPush: false }));
    users.forEach((u, idx) => {
      if (idx < 3) {
        mapped[idx] = {
          user: u.user_id,
          sendEmail: u.is_email === 1,
          sendPush: u.is_push_notification === 1
        };
      }
    });
    return mapped;
  };

  useEffect(() => {
    if (reminderData) {
      const loaded = mapReminderUsers(reminderData[`${reminderType}_users`]);
      setReminders(loaded);

      setAllReminders((prev) => ({
        ...prev,
        [reminderType]: loaded
      }));

      setReminderMinutes(reminderData[`${reminderType}_duration`] || 0);
    }
  }, [reminderType, reminderData]);


  const handleFacilityChange = async (selectedOption) => {
    setSelectedFacility(selectedOption);
    setLoading(true);
    setError("");

    let tokenStr = localStorage.getItem("accessToken");

    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/users/get-group-users`,
        {},
        { headers: { Authorization: `Bearer ${tokenStr}` } }
      );

      const facilityData = data.facilities;
      const selectedFacilityUsers = facilityData[selectedOption.value]?.users || [];

      if (selectedFacilityUsers.length === 0) {
        setError("No users found for the selected facility.");
      }

      const mappedUsers = selectedFacilityUsers.map((user) => (
        {
          value: user.id,
          label: `${user.last_name} ${user.first_name}`,
          has_mobile_app: user.has_mobile_app
        }));

      setUsers(mappedUsers);

      const reminderRes = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/medpass/get-medpass-reminders/` + selectedOption.value,
        { headers: { Authorization: `Bearer ${tokenStr}` } }
      );

      const reminderData = reminderRes.data?.reminder;
      if (reminderData) {
        const reminder_1 = mapReminderUsers(reminderData.reminder_1_users);
        const reminder_2 = mapReminderUsers(reminderData.reminder_2_users);
        const reminder_3 = mapReminderUsers(reminderData.reminder_3_users);

        setAllReminders({
          reminder_1,
          reminder_2,
          reminder_3
        });

        setReminderData(reminderData);
        setReminders(reminder_1);
        setReminderType("reminder_1");
        setReminderMinutes(reminderData.reminder_1_duration || 0);
      }

    } catch (error) {
      setError("Error fetching users or reminders. Please try again.");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReminderChange = (index, field, value) => {
    const updated = [...reminders];
    updated[index][field] = value;
    setReminders(updated);
    setAllReminders((prev) => {
      const copy = { ...prev };
      copy[reminderType] = updated;
      return copy;
    });
  };


  const handleUserSelect = (index, selectedOption) => {
    const updated = [...reminders];
    if (selectedOption) {
      updated[index].user = selectedOption.value;
      updated[index].sendPush = selectedOption.has_mobile_app === 1;
    } else {
      updated[index] = { user: null, sendEmail: false, sendPush: false };
    }
    setReminders(updated);
    setAllReminders((prev) => {
      const copy = { ...prev };
      copy[reminderType] = updated;
      return copy;
    });
  };

  const handleSaveSettings = async () => {
    const tokenStr = localStorage.getItem("accessToken");
    const facility_id = selectedFacility?.value;

    const buildPayload = (reminderArr) =>
      reminderArr
        .filter((r) => r.user !== null)
        .map((r) => ({
          user_id: r.user,
          is_push_notification: r.sendPush ? 1 : 0,
          is_email: r.sendEmail ? 1 : 0,
        }));

    const payload = {
      facility_id,
      reminder_1_users: buildPayload(allReminders.reminder_1),
      reminder_2_users: buildPayload(allReminders.reminder_2),
      reminder_3_users: buildPayload(allReminders.reminder_3),
    };
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/medpass/update-medpass-reminders`,
        payload,
        { headers: { Authorization: `Bearer ${tokenStr}` } }
      );
      toast.success(data.message);
    } catch (err) {
      toast.error(data.message);
    }
  };

  const reminderOptions = [
    { value: "reminder_1", label: "Remind Before" },
    { value: "reminder_2", label: "Remind On Time" },
    { value: "reminder_3", label: "Remind After" }
  ];

  return (
    <div className="p-3">
      <Form>
        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold">Select Facility</Form.Label>
          <ThemedSelect
            options={Object.values(facilities).map((facility) => ({
              value: facility.value,
              label: facility.key
            }))}
            onChange={handleFacilityChange}
            value={selectedFacility}
            placeholder="Choose a Facility..."
          />
        </Form.Group>

        {loading && (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" />
            <p className="mt-2 text-secondary">Loading users...</p>
          </div>
        )}

        {error && <Alert variant="danger">{error}</Alert>}

        {selectedFacility && !loading && !error && (
          <>
            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold">Reminder Timing Type</Form.Label>
              <ThemedSelect
                options={reminderOptions}
                value={reminderOptions.find(opt => opt.value === reminderType)}
                onChange={(option) => setReminderType(option.value)}
                placeholder="Select Reminder Type"
              />
            </Form.Group>
            {reminders.map((reminder, index) => (
              <Row key={index} className="mb-3 align-items-center">
                <Col md={5}>
                  <Form.Label className="fw-semibold">{`User ${index + 1}`}</Form.Label>
                  <ThemedSelect
                    options={[
                      { value: null, label: "Select a User..." },
                      ...users.filter(
                        (user) =>
                          !reminders.some(
                            (r, i) => i !== index && String(r.user) === String(user.value)
                          )
                      ),
                    ]}
                    value={
                      reminder.user !== null
                        ? users.find((user) => String(user.value) === String(reminder.user)) || null
                        : { value: null, label: "Select a User..." }
                    }
                    onChange={(selectedOption) => handleUserSelect(index, selectedOption)}
                    placeholder="Select User..."
                  />
                </Col>
                <Col md={3} className="d-flex align-items-center justify-content-center">
                  <Form.Check
                    type="checkbox"
                    label="Send Email"
                    className="fw-semibold"
                    checked={reminder.sendEmail}
                    onChange={(e) => handleReminderChange(index, "sendEmail", e.target.checked)}
                  />
                </Col>
                <Col md={3} className="d-flex align-items-center justify-content-center">
                  <Form.Check
                    type="checkbox"
                    label="Push Notification"
                    className="fw-semibold"
                    checked={reminder.sendPush}
                    onChange={(e) => handleReminderChange(index, "sendPush", e.target.checked)}
                  />
                </Col>
              </Row>
            ))}

            {(reminderType === "reminder_1" || reminderType === "reminder_3") && (
              <Form.Group className="mt-2">
                <Form.Label className="fw-semibold">Minutes</Form.Label>
                <Form.Control
                  type="number"
                  value={reminderMinutes}
                  onChange={(e) => setReminderMinutes(Number(e.target.value))}
                  min="1"
                />
              </Form.Group>
            )}

            <div className="mt-4 text-end">
              <Button variant="primary" className="px-4 fw-semibold" onClick={handleSaveSettings}>
                Save Settings
              </Button>
            </div>
          </>
        )}
      </Form>
    </div>
  );
};

export default MobileNotification;